import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { DollarSign, FileText, Users, CreditCard, TrendingUp, ChevronDown, ChevronUp, Building2, Printer, Download, CheckCircle, Calendar } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/CuratorBilling.css';

// Fallback defaults (used when org has no custom rates set)
const DEFAULT_SUBMISSION_FEE = 4;
const DEFAULT_MEMBERSHIP_FEE = 5;
const DEFAULT_STRIPE_PERCENT = 2.9;
const DEFAULT_STRIPE_FIXED = 0.30;
const TEST_EMAIL_DOMAIN = '@vibrantcast.com'; // Filter out internal test accounts

const CuratorBilling = () => {
  const { selectedOrganization, user } = useOutletContext() || {};
  const [submissions, setSubmissions] = useState([]);
  const [submissionArtists, setSubmissionArtists] = useState({}); // { submissionId: [artist details] }
  const [memberships, setMemberships] = useState([]);
  const [orgDetails, setOrgDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [loadingArtists, setLoadingArtists] = useState({});
  const [exclusions, setExclusions] = useState({}); // { 'submissionId-mappingId': true }
  const [isAdmin, setIsAdmin] = useState(false);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const [revenueData, setRevenueData] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(false);

  // Reconciliation state
  const [reconciliations, setReconciliations] = useState([]);
  const [showReconcileForm, setShowReconcileForm] = useState(false);
  const [reconcileData, setReconcileData] = useState({ period_month: '', quickbooks_invoice_number: '', notes: '', amount_paid: '' });
  const [reconcileSaving, setReconcileSaving] = useState(false);

  // Month filter state — range-based
  const [startMonth, setStartMonth] = useState(''); // '' = no start bound
  const [endMonth, setEndMonth] = useState('');     // '' = no end bound
  // Derived: for reconciliation form compat
  const selectedMonth = startMonth === endMonth ? startMonth : '';

  // Compute date range for the selected month filter
  const getMonthRange = () => {
    if (!startMonth && !endMonth) return null;
    let start = null, startDate = null;
    let end = null, endDate = null;
    if (startMonth) {
      const [y, m] = startMonth.split('-').map(Number);
      start = `${y}-${String(m).padStart(2, '0')}-01T00:00:00Z`;
      startDate = `${y}-${String(m).padStart(2, '0')}-01`;
    }
    if (endMonth) {
      const [y, m] = endMonth.split('-').map(Number);
      const nm = m === 12 ? 1 : m + 1;
      const ny = m === 12 ? y + 1 : y;
      end = `${ny}-${String(nm).padStart(2, '0')}-01T00:00:00Z`;
      endDate = `${ny}-${String(nm).padStart(2, '0')}-01`;
    }
    return { start, end, startDate, endDate };
  };

  // Check if current user is a VibrantCast admin (role_id = 3)
  useEffect(() => {
    const checkAdmin = async () => {
      if (!user?.id) return;
      const { data } = await supabase
        .from('user_roles')
        .select('role_id')
        .eq('user_id', user.id)
        .eq('role_id', 3)
        .maybeSingle();
      setIsAdmin(!!data);
    };
    checkAdmin();
  }, [user?.id]);

  useEffect(() => {
    if (!selectedOrganization?.organization_id) return;
    // Check if this org is the platform owner
    const checkPlatformOwner = async () => {
      const { data } = await supabase
        .from('organizations')
        .select('is_platform_owner')
        .eq('organization_id', selectedOrganization.organization_id)
        .single();
      const isOwner = !!data?.is_platform_owner;
      setIsPlatformOwner(isOwner);
      if (isOwner) {
        fetchRevenueData();
      } else {
        fetchBillingData();
      }
    };
    checkPlatformOwner();
  }, [selectedOrganization?.organization_id]);

  // Refetch billing data when month filter changes
  useEffect(() => {
    if (!selectedOrganization?.organization_id || isPlatformOwner) return;
    setExpandedSubmission(null);
    setSubmissionArtists({});
    fetchBillingData();
  }, [startMonth, endMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      const orgId = selectedOrganization.organization_id;

      const monthRange = getMonthRange();

      // Build memberships query with optional month filter
      let membershipsQuery = supabase
        .from('external_organization_members')
        .select('id, first_name, last_name, email, membership_status, join_date, created_at, import_source')
        .eq('organization_id', orgId)
        .eq('import_source', 'stripe_checkout');
      if (monthRange?.startDate) {
        membershipsQuery = membershipsQuery.gte('join_date', monthRange.startDate);
      }
      if (monthRange?.endDate) {
        membershipsQuery = membershipsQuery.lt('join_date', monthRange.endDate);
      }

      const [membershipsResult, orgResult] = await Promise.all([
        membershipsQuery.order('created_at', { ascending: false }),

        // Get org details including membership fee and billing rates
        supabase
          .from('organizations')
          .select('membership_fee, membership_period, name, billing_email, platform_submission_fee, platform_membership_fee, stripe_fee_percent, stripe_fee_fixed, pass_stripe_fees_to_org')
          .eq('organization_id', orgId)
          .single()
      ]);

      // Fetch submission data and exclusions
      await Promise.all([
        fetchSubmissionsFallback(orgId),
        fetchExclusions(orgId),
      ]);

      if (!membershipsResult.error) {
        const filtered = (membershipsResult.data || []).filter(
          m => !m.email?.toLowerCase().endsWith(TEST_EMAIL_DOMAIN)
        );
        setMemberships(filtered);
      }

      if (!orgResult.error) {
        setOrgDetails(orgResult.data);
      }

      // Fetch reconciliation history
      await fetchReconciliations(orgId);
    } catch (err) {
      console.error('Error fetching billing data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Revenue dashboard: fetch billing data across ALL client organizations
  const fetchRevenueData = async () => {
    setRevenueLoading(true);
    setLoading(true);
    try {
      // Get all non-platform-owner orgs
      const { data: orgs } = await supabase
        .from('organizations')
        .select('organization_id, name, slug, membership_fee, is_platform_owner, platform_submission_fee, platform_membership_fee, stripe_fee_percent, stripe_fee_fixed, pass_stripe_fees_to_org')
        .order('organization_id');

      const clientOrgs = (orgs || []).filter(o => !o.is_platform_owner);

      // For each client org, gather billing totals
      const orgBilling = await Promise.all(
        clientOrgs.map(async (org) => {
          const orgId = org.organization_id;
          const membershipFee = parseFloat(org.membership_fee) || 0;
          const orgSubFee = parseFloat(org.platform_submission_fee) ?? DEFAULT_SUBMISSION_FEE;
          const orgMemFee = parseFloat(org.platform_membership_fee) ?? DEFAULT_MEMBERSHIP_FEE;
          const orgStripePercent = (parseFloat(org.stripe_fee_percent) ?? DEFAULT_STRIPE_PERCENT) / 100;
          const orgStripeFixed = parseFloat(org.stripe_fee_fixed) ?? DEFAULT_STRIPE_FIXED;
          const orgPassStripe = org.pass_stripe_fees_to_org ?? true;

          // Submissions
          const { data: subs } = await supabase
            .from('submissions_details')
            .select('submission_id, title, submission_price, member_free, status')
            .eq('organization_id', orgId);

          let totalSubmissionFees = 0;
          let totalCollected = 0;
          let totalStripeFees = 0;
          let totalBillableArtists = 0;
          const submissionDetails = [];

          for (const sub of (subs || [])) {
            const isFree = sub.member_free && (!sub.submission_price || parseFloat(sub.submission_price) === 0);
            const price = parseFloat(sub.submission_price) || 0;

            const { data: mappings } = await supabase
              .from('artist_submission_mapping')
              .select('id, user_id, paid')
              .eq('submission_id', sub.submission_id);

            if (!mappings || mappings.length === 0) {
              submissionDetails.push({ ...sub, billableArtists: 0, platformFee: 0 });
              continue;
            }

            // Filter test accounts
            const userIds = [...new Set(mappings.map(m => m.user_id).filter(Boolean))];
            let testUserIds = new Set();
            if (userIds.length > 0) {
              const { data: users } = await supabase
                .from('users')
                .select('uuid, email')
                .in('uuid', userIds);
              if (users) {
                users.forEach(u => {
                  if (u.email?.toLowerCase().endsWith(TEST_EMAIL_DOMAIN)) testUserIds.add(u.uuid);
                });
              }
            }

            // Filter exclusions
            const { data: excls } = await supabase
              .from('billing_exclusions')
              .select('mapping_id')
              .eq('organization_id', orgId)
              .eq('submission_id', sub.submission_id);
            const excludedIds = new Set((excls || []).map(e => e.mapping_id));

            const real = mappings.filter(m => !testUserIds.has(m.user_id) && !excludedIds.has(m.id));
            const billableArtists = isFree ? real.length : real.filter(m => m.paid).length;
            const paidArtists = real.filter(m => m.paid).length;
            const fee = billableArtists * orgSubFee;
            const collected = isFree ? 0 : paidArtists * price;
            const stripe = isFree ? 0 : paidArtists * (price * orgStripePercent + orgStripeFixed);

            totalSubmissionFees += fee;
            totalCollected += collected;
            totalStripeFees += stripe;
            totalBillableArtists += billableArtists;

            submissionDetails.push({ ...sub, billableArtists, platformFee: fee, isFree });
          }

          // Memberships
          const { data: members } = await supabase
            .from('external_organization_members')
            .select('id, email')
            .eq('organization_id', orgId)
            .eq('import_source', 'stripe_checkout');

          const realMembers = (members || []).filter(
            m => !m.email?.toLowerCase().endsWith(TEST_EMAIL_DOMAIN)
          );
          const memberCount = realMembers.length;
          const memberPlatformFee = memberCount * orgMemFee;
          const memberCollected = memberCount * membershipFee;
          const memberStripe = memberCount * (membershipFee * orgStripePercent + orgStripeFixed);

          totalCollected += memberCollected;
          totalStripeFees += memberStripe;

          return {
            org,
            submissionDetails,
            totalSubmissionFees,
            memberCount,
            memberPlatformFee,
            memberCollected,
            totalCollected,
            totalStripeFees: Math.round(totalStripeFees * 100) / 100,
            totalPlatformFees: totalSubmissionFees + memberPlatformFee,
            totalBillableArtists,
          };
        })
      );

      // Grand totals
      const grandTotals = orgBilling.reduce((acc, o) => ({
        platformFees: acc.platformFees + o.totalPlatformFees,
        collected: acc.collected + o.totalCollected,
        stripeFees: acc.stripeFees + o.totalStripeFees,
        submissionFees: acc.submissionFees + o.totalSubmissionFees,
        membershipFees: acc.membershipFees + o.memberPlatformFee,
        memberships: acc.memberships + o.memberCount,
        artists: acc.artists + o.totalBillableArtists,
      }), { platformFees: 0, collected: 0, stripeFees: 0, submissionFees: 0, membershipFees: 0, memberships: 0, artists: 0 });

      setRevenueData({ orgBilling, grandTotals });
    } catch (err) {
      console.error('Error fetching revenue data:', err);
    } finally {
      setRevenueLoading(false);
      setLoading(false);
    }
  };

  const fetchSubmissionsFallback = async (orgId) => {
    const monthRange = getMonthRange();
    // Get submissions for this org
    const { data: subs, error: subsError } = await supabase
      .from('submissions_details')
      .select('submission_id, collection_id, title, submission_price, status, member_free, stripe_product_id')
      .eq('organization_id', orgId)
      .order('submission_id');

    if (subsError) {
      console.error('Error fetching submissions:', subsError);
      return;
    }

    // For each submission, fetch artist mappings with user email to filter test accounts
    const enriched = await Promise.all(
      (subs || []).map(async (sub) => {
        // Get all artist mappings for this submission (filtered by month if set)
        let mappingsQuery = supabase
          .from('artist_submission_mapping')
          .select('id, user_id, paid, created_at, submitted_at')
          .eq('submission_id', sub.submission_id);
        if (monthRange?.start) {
          mappingsQuery = mappingsQuery.gte('created_at', monthRange.start);
        }
        if (monthRange?.end) {
          mappingsQuery = mappingsQuery.lt('created_at', monthRange.end);
        }
        const { data: mappings } = await mappingsQuery;

        if (!mappings || mappings.length === 0) {
          return { ...sub, total_artists: 0, paid_artists: 0 };
        }

        // Look up emails to filter test accounts
        const userIds = [...new Set(mappings.map(m => m.user_id).filter(Boolean))];
        let testUserIds = new Set();
        if (userIds.length > 0) {
          const { data: users } = await supabase
            .from('users')
            .select('uuid, email')
            .in('uuid', userIds);
          if (users) {
            users.forEach(u => {
              if (u.email && u.email.toLowerCase().endsWith(TEST_EMAIL_DOMAIN)) {
                testUserIds.add(u.uuid);
              }
            });
          }
        }

        // Filter out test accounts
        const realMappings = mappings.filter(m => !testUserIds.has(m.user_id));

        return {
          ...sub,
          total_artists: realMappings.length,
          paid_artists: realMappings.filter(m => m.paid).length,
        };
      })
    );

    setSubmissions(enriched);
  };

  // Fetch existing billing exclusions for this org
  const fetchExclusions = async (orgId) => {
    const { data, error } = await supabase
      .from('billing_exclusions')
      .select('submission_id, mapping_id')
      .eq('organization_id', orgId);

    if (!error && data) {
      const map = {};
      data.forEach(e => { map[`${e.submission_id}-${e.mapping_id}`] = true; });
      setExclusions(map);
    }
  };

  // Toggle billing exclusion for a specific artist mapping
  const toggleExclusion = async (submissionId, mappingId, artistName) => {
    const key = `${submissionId}-${mappingId}`;
    const orgId = selectedOrganization.organization_id;
    const isCurrentlyExcluded = exclusions[key];

    if (isCurrentlyExcluded) {
      // Remove exclusion
      await supabase
        .from('billing_exclusions')
        .delete()
        .eq('organization_id', orgId)
        .eq('submission_id', submissionId)
        .eq('mapping_id', mappingId);

      setExclusions(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } else {
      // Add exclusion
      await supabase
        .from('billing_exclusions')
        .insert({
          organization_id: orgId,
          submission_id: submissionId,
          mapping_id: mappingId,
          excluded_by: user?.id || null,
          reason: `Excluded by admin: ${artistName}`,
        });

      setExclusions(prev => ({ ...prev, [key]: true }));
    }
  };

  // Lazy-load individual artist details when a submission row is expanded
  const fetchArtistsForSubmission = async (submissionId, isFree) => {
    if (submissionArtists[submissionId]) return; // already loaded
    setLoadingArtists(prev => ({ ...prev, [submissionId]: true }));

    try {
      // Get all artist_submission_mapping rows for this submission (filtered by month if set)
      const monthRange = getMonthRange();
      let mappingsQuery = supabase
        .from('artist_submission_mapping')
        .select('id, user_id, paid, created_at, submitted_at')
        .eq('submission_id', submissionId);
      if (monthRange?.start) {
        mappingsQuery = mappingsQuery.gte('created_at', monthRange.start);
      }
      if (monthRange?.end) {
        mappingsQuery = mappingsQuery.lt('created_at', monthRange.end);
      }
      const { data: mappings, error } = await mappingsQuery
        .order('created_at', { ascending: true });

      if (error || !mappings) {
        console.error('Error fetching artist mappings:', error);
        setSubmissionArtists(prev => ({ ...prev, [submissionId]: [] }));
        return;
      }

      // Find the collection_id for this submission (to look up accepted artworks)
      const sub = submissions.find(s => s.submission_id === submissionId);
      const collectionId = sub?.collection_id;

      // Collect unique user_ids to look up names
      const userIds = [...new Set(mappings.map(m => m.user_id).filter(Boolean))];

      // Fetch user info and accepted artwork counts in parallel
      let usersMap = {};
      let acceptedMap = {}; // { user_uuid: { accepted, total } }
      if (userIds.length > 0) {
        const { data: users } = await supabase
          .from('users')
          .select('uuid, first_name, last_name, email')
          .in('uuid', userIds);

        if (users) {
          users.forEach(u => { usersMap[u.uuid] = u; });
        }

        // Fetch accepted artworks per artist via two queries (no FK for Supabase join)
        if (collectionId) {
          // 1. Get all vc_ids + accepted status for this collection
          const { data: cvmData } = await supabase
            .from('collection_vc_mapping')
            .select('vc_id, accepted')
            .eq('collection_id', collectionId);

          if (cvmData && cvmData.length > 0) {
            const vcIds = cvmData.map(r => r.vc_id);
            // 2. Look up which user owns each vc
            const { data: vcOwners } = await supabase
              .from('vcs')
              .select('vc_id, uuid')
              .in('vc_id', vcIds);

            if (vcOwners) {
              const vcOwnerMap = {};
              vcOwners.forEach(v => { vcOwnerMap[v.vc_id] = v.uuid; });

              cvmData.forEach(row => {
                const uuid = vcOwnerMap[row.vc_id];
                if (!uuid) return;
                if (!acceptedMap[uuid]) acceptedMap[uuid] = { accepted: 0, total: 0 };
                acceptedMap[uuid].total += 1;
                if (row.accepted) acceptedMap[uuid].accepted += 1;
              });
            }
          }
        }
      }

      // Merge user info into mappings, filtering out test accounts
      const enrichedArtists = mappings
        .map(m => {
          const user = usersMap[m.user_id] || {};
          const artworkInfo = acceptedMap[m.user_id] || { accepted: 0, total: 0 };
          return {
            ...m,
            first_name: user.first_name || '—',
            last_name: user.last_name || '',
            email: user.email || '—',
            acceptedArtworks: artworkInfo.accepted,
            totalArtworks: artworkInfo.total,
          };
        })
        .filter(a => !a.email.toLowerCase().endsWith(TEST_EMAIL_DOMAIN));

      setSubmissionArtists(prev => ({ ...prev, [submissionId]: enrichedArtists }));
    } finally {
      setLoadingArtists(prev => ({ ...prev, [submissionId]: false }));
    }
  };

  // When a submission is expanded, fetch its artists
  const handleToggleSubmission = (submissionId, isFree) => {
    const isExpanding = expandedSubmission !== submissionId;
    setExpandedSubmission(isExpanding ? submissionId : null);
    if (isExpanding) {
      fetchArtistsForSubmission(submissionId, isFree);
    }
  };

  // Calculate billing summary (reacts to exclusion changes)
  const billingData = useMemo(() => {
    const membershipFee = parseFloat(orgDetails?.membership_fee) || 50;

    // Dynamic per-org billing rates
    const subFee = parseFloat(orgDetails?.platform_submission_fee) || DEFAULT_SUBMISSION_FEE;
    const memFee = parseFloat(orgDetails?.platform_membership_fee) || DEFAULT_MEMBERSHIP_FEE;
    const stripePercent = (parseFloat(orgDetails?.stripe_fee_percent) || DEFAULT_STRIPE_PERCENT) / 100;
    const stripeFixed = parseFloat(orgDetails?.stripe_fee_fixed) ?? DEFAULT_STRIPE_FIXED;
    const passStripe = orgDetails?.pass_stripe_fees_to_org ?? true;

    // Submission line items — factor in exclusions
    const submissionLineItems = submissions.map(sub => {
      const isFree = sub.member_free && (!sub.submission_price || parseFloat(sub.submission_price) === 0);
      const submissionPrice = parseFloat(sub.submission_price) || 0;

      // If we have loaded artist details, compute from them minus exclusions
      const artists = submissionArtists[sub.submission_id];
      let artistCount, paidArtists;
      if (artists) {
        const included = artists.filter(a => !exclusions[`${sub.submission_id}-${a.id}`]);
        artistCount = included.length;
        paidArtists = included.filter(a => a.paid).length;
      } else {
        // Fallback: subtract known exclusions for this submission from pre-fetched counts
        const excludedForSub = Object.keys(exclusions).filter(k => k.startsWith(`${sub.submission_id}-`)).length;
        artistCount = Math.max(0, (sub.total_artists || 0) - excludedForSub);
        paidArtists = Math.max(0, (sub.paid_artists || 0) - excludedForSub);
      }

      // Platform fee per billable artist (dynamic rate)
      // Free shows: all non-excluded artists → org owes us per-artist fee
      // Paid shows: only paid artists → we collected on their behalf, keep per-artist fee
      const billableArtists = isFree ? artistCount : paidArtists;
      const platformFee = billableArtists * subFee;

      // Total collected from artists via Stripe (for paid shows only)
      const totalCollected = isFree ? 0 : paidArtists * submissionPrice;

      // Stripe fees on submission payments
      const stripeFee = isFree ? 0 : paidArtists * (submissionPrice * stripePercent + stripeFixed);

      // Settlement
      // passStripe=true: Stripe fees deducted from org's share
      // passStripe=false: VibrantCast absorbs Stripe fees
      const stripeDeduction = passStripe ? Math.round(stripeFee * 100) / 100 : 0;
      const owedToOrg = isFree ? 0 : Math.max(0, totalCollected - platformFee - stripeDeduction);
      const owedToUs = isFree ? platformFee : 0; // org owes us for free shows

      return {
        type: 'submission',
        id: sub.submission_id,
        collectionId: sub.collection_id,
        title: sub.title,
        status: sub.status,
        isFree,
        submissionPrice,
        artistCount,
        paidArtists,
        billableArtists,
        platformFee,
        totalCollected,
        stripeFee: Math.round(stripeFee * 100) / 100,
        owedToOrg,
        owedToUs,
      };
    });

    // Membership line items (dynamic rate)
    const membershipCount = memberships.length;
    const membershipTotalCollected = membershipCount * membershipFee;
    const membershipPlatformFee = membershipCount * memFee;
    const membershipStripeFee = membershipCount * (membershipFee * stripePercent + stripeFixed);
    const memberStripeDeduction = passStripe ? Math.round(membershipStripeFee * 100) / 100 : 0;
    const membershipOwedToOrg = membershipTotalCollected - membershipPlatformFee - memberStripeDeduction;

    // Totals
    const totalPlatformFees = submissionLineItems.reduce((sum, item) => sum + item.platformFee, 0) + membershipPlatformFee;
    const totalCollected = submissionLineItems.reduce((sum, item) => sum + item.totalCollected, 0) + membershipTotalCollected;
    const totalStripeFees = submissionLineItems.reduce((sum, item) => sum + item.stripeFee, 0) + Math.round(membershipStripeFee * 100) / 100;

    // Settlement
    const totalOwedToOrg = submissionLineItems.reduce((sum, item) => sum + item.owedToOrg, 0) + membershipOwedToOrg;
    const totalOwedToUs = submissionLineItems.reduce((sum, item) => sum + item.owedToUs, 0);
    const netSettlement = totalOwedToUs - totalOwedToOrg;

    return {
      submissionLineItems,
      membershipCount,
      membershipFee,
      membershipTotalCollected,
      membershipPlatformFee,
      membershipStripeFee: Math.round(membershipStripeFee * 100) / 100,
      membershipOwedToOrg: Math.round(membershipOwedToOrg * 100) / 100,
      totalPlatformFees,
      totalCollected,
      totalStripeFees,
      stripeAbsorbed: passStripe ? 0 : Math.round(totalStripeFees * 100) / 100,
      stripePassedToOrg: passStripe ? Math.round(totalStripeFees * 100) / 100 : 0,
      passStripe,
      subFee,
      memFee,
      totalOwedToOrg: Math.round(totalOwedToOrg * 100) / 100,
      totalOwedToUs,
      netSettlement: Math.round(netSettlement * 100) / 100,
    };
  }, [submissions, memberships, orgDetails, exclusions, submissionArtists]);

  // Total payments already made to org (from reconciliation records)
  const totalPaid = useMemo(
    () => reconciliations.reduce((sum, r) => sum + (parseFloat(r.amount_paid) || 0), 0),
    [reconciliations]
  );
  const outstandingBalance = Math.round((billingData.netSettlement + totalPaid) * 100) / 100;

  // ─── RECONCILIATION ──────────────────────────────────────────────────

  const fetchReconciliations = async (orgId) => {
    const { data, error } = await supabase
      .from('billing_reconciliations')
      .select('*')
      .eq('organization_id', orgId)
      .order('period_month', { ascending: false });
    if (!error && data) setReconciliations(data);
  };

  const saveReconciliation = async () => {
    if (!reconcileData.period_month) return;
    setReconcileSaving(true);
    try {
      const { error } = await supabase
        .from('billing_reconciliations')
        .upsert({
          organization_id: selectedOrganization.organization_id,
          period_month: reconcileData.period_month,
          reconciled_by: user?.id || null,
          reconciled_at: new Date().toISOString(),
          quickbooks_invoice_number: reconcileData.quickbooks_invoice_number || null,
          notes: reconcileData.notes || null,
          total_collected: billingData.totalCollected,
          platform_fees: billingData.totalPlatformFees,
          stripe_fees: billingData.totalStripeFees,
          owed_to_org: billingData.totalOwedToOrg,
          owed_to_us: billingData.totalOwedToUs,
          net_settlement: billingData.netSettlement,
          amount_paid: parseFloat(reconcileData.amount_paid) || 0,
        }, { onConflict: 'organization_id,period_month' });
      if (error) throw error;
      await fetchReconciliations(selectedOrganization.organization_id);
      setShowReconcileForm(false);
      setReconcileData({ period_month: '', quickbooks_invoice_number: '', notes: '', amount_paid: '' });
    } catch (err) {
      console.error('Error saving reconciliation:', err);
      alert('Error saving reconciliation: ' + err.message);
    } finally {
      setReconcileSaving(false);
    }
  };

  // ─── PRINT ────────────────────────────────────────────────────────────

  const handlePrint = () => {
    window.print();
  };

  // ─── CSV / EXCEL EXPORT ───────────────────────────────────────────────

  const exportCSV = () => {
    const orgName = selectedOrganization?.name || 'billing';
    const escapeCSV = (val) => {
      const str = String(val ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // Build rows: submissions + memberships + totals
    const rows = [];

    // Submissions
    rows.push(['--- Submission Processing Fees ---', '', '', '', '', '', '', '']);
    rows.push(['Call Out / Exhibition', 'Status', 'Fee Type', 'Artists', 'Collected', 'Platform Fee', `Due to ${orgName}`, 'Stripe Fee']);
    billingData.submissionLineItems.forEach(item => {
      rows.push([
        item.title,
        item.status,
        item.isFree ? 'Free (Member)' : `$${item.submissionPrice.toFixed(2)}`,
        item.billableArtists,
        `$${item.totalCollected.toFixed(2)}`,
        `$${item.platformFee.toFixed(2)}`,
        `$${item.owedToOrg.toFixed(2)}`,
        `$${item.stripeFee.toFixed(2)}`,
      ]);
    });
    const subTotals = billingData.submissionLineItems.reduce((a, i) => ({
      artists: a.artists + i.billableArtists,
      collected: a.collected + i.totalCollected,
      platform: a.platform + i.platformFee,
      owed: a.owed + i.owedToOrg,
      stripe: a.stripe + i.stripeFee,
    }), { artists: 0, collected: 0, platform: 0, owed: 0, stripe: 0 });
    rows.push(['Submission Totals', '', '', subTotals.artists, `$${subTotals.collected.toFixed(2)}`, `$${subTotals.platform.toFixed(2)}`, `$${subTotals.owed.toFixed(2)}`, `$${subTotals.stripe.toFixed(2)}`]);
    rows.push([]);

    // Memberships
    rows.push(['--- Membership Processing Fees ---', '', '', '', '', '']);
    rows.push(['Member', 'Email', 'Join Date', 'Status', 'Membership Fee', 'Platform Fee']);
    memberships.forEach(m => {
      rows.push([
        `${m.first_name || ''} ${m.last_name || ''}`.trim(),
        m.email,
        m.join_date ? new Date(m.join_date).toLocaleDateString() : '',
        m.membership_status,
        `$${billingData.membershipFee.toFixed(2)}`,
        `$${billingData.memFee.toFixed(2)}`,
      ]);
    });
    rows.push([`Membership Totals (${billingData.membershipCount} sold)`, '', '', '', `$${billingData.membershipTotalCollected.toFixed(2)}`, `$${billingData.membershipPlatformFee.toFixed(2)}`]);
    rows.push([]);

    // Period info
    rows.push(['Statement Period', startMonth || endMonth ? `${startMonth || 'beginning'} through ${endMonth || 'present'}` : 'All Time']);
    rows.push([]);

    // Settlement summary
    rows.push(['--- Settlement Summary ---']);
    rows.push(['Collected via Stripe', `$${billingData.totalCollected.toFixed(2)}`]);
    rows.push(['VibrantCast Platform Fees', `$${billingData.totalPlatformFees.toFixed(2)}`]);
    rows.push(['Stripe Processing Fees', `$${billingData.totalStripeFees.toFixed(2)}`]);
    if (billingData.totalOwedToOrg > 0) rows.push([`Owed to ${orgName}`, `$${billingData.totalOwedToOrg.toFixed(2)}`]);
    if (billingData.totalOwedToUs > 0) rows.push([`${orgName} Owes VibrantCast`, `$${billingData.totalOwedToUs.toFixed(2)}`]);
    rows.push(['Net Settlement', `$${Math.abs(billingData.netSettlement).toFixed(2)}`, billingData.netSettlement > 0 ? `${orgName} owes VibrantCast` : billingData.netSettlement < 0 ? `VibrantCast owes ${orgName}` : 'Settled']);

    const csvContent = rows.map(row => row.map(escapeCSV).join(',')).join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const periodSuffix = endMonth || startMonth || new Date().toISOString().split('T')[0];
    link.download = `${orgName.replace(/[^a-z0-9]/gi, '_')}_billing_${periodSuffix}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  if (loading) {
    return (
      <div className="billing-container">
        <div className="billing-loading">
          <div className="loading-spinner"></div>
          <p>Loading billing data...</p>
        </div>
      </div>
    );
  }

  if (!selectedOrganization) {
    return (
      <div className="billing-container">
        <div className="billing-empty">
          <DollarSign size={48} />
          <h2>Select an Organization</h2>
          <p>Choose an organization from the menu to view billing information.</p>
        </div>
      </div>
    );
  }

  // ─── REVENUE DASHBOARD (Platform Owner view) ─────────────────────────
  if (isPlatformOwner) {
    return (
      <div className="billing-container">
        <div className="billing-header">
          <div className="billing-header-left">
            <h1><TrendingUp size={24} /> Revenue Dashboard</h1>
            <p className="billing-subtitle">
              VibrantCast &mdash; Platform revenue across all client organizations
            </p>
          </div>
        </div>

        {!revenueData ? (
          <div className="billing-loading">
            <div className="loading-spinner"></div>
            <p>Loading revenue data...</p>
          </div>
        ) : (
          <>
            {/* Grand Total Cards */}
            <div className="billing-summary-cards">
              <div className="billing-card">
                <div className="billing-card-icon submissions">
                  <FileText size={20} />
                </div>
                <div className="billing-card-content">
                  <span className="billing-card-label">Submission Revenue</span>
                  <span className="billing-card-value">
                    {formatCurrency(revenueData.grandTotals.submissionFees)}
                  </span>
                </div>
              </div>
              <div className="billing-card">
                <div className="billing-card-icon memberships">
                  <Users size={20} />
                </div>
                <div className="billing-card-content">
                  <span className="billing-card-label">Membership Revenue</span>
                  <span className="billing-card-value">
                    {formatCurrency(revenueData.grandTotals.membershipFees)}
                  </span>
                </div>
              </div>
              <div className="billing-card">
                <div className="billing-card-icon total">
                  <TrendingUp size={20} />
                </div>
                <div className="billing-card-content">
                  <span className="billing-card-label">Total Platform Revenue</span>
                  <span className="billing-card-value highlight">
                    {formatCurrency(revenueData.grandTotals.platformFees)}
                  </span>
                </div>
              </div>
              <div className="billing-card">
                <div className="billing-card-icon stripe">
                  <CreditCard size={20} />
                </div>
                <div className="billing-card-content">
                  <span className="billing-card-label">Stripe Fees (Absorbed)</span>
                  <span className="billing-card-value muted">
                    {formatCurrency(revenueData.grandTotals.stripeFees)}
                  </span>
                </div>
              </div>
            </div>

            {/* Per-Organization Breakdown */}
            <div className="billing-section">
              <h2 className="billing-section-title">
                <Building2 size={18} /> Revenue by Organization
              </h2>
              <p className="billing-section-desc">
                Platform fees earned from each client organization
              </p>
              <div className="billing-table-wrapper">
                <table className="billing-table">
                  <thead>
                    <tr>
                      <th className="text-left">Organization</th>
                      <th className="text-right">Billable Artists</th>
                      <th className="text-right">Submission Fees</th>
                      <th className="text-right">Memberships</th>
                      <th className="text-right">Membership Fees</th>
                      <th className="text-right">Total Platform Fees</th>
                      <th className="text-right">Stripe Fees</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenueData.orgBilling.map((ob) => (
                      <tr key={ob.org.organization_id} className="billing-row">
                        <td className="text-left">
                          <div className="billing-row-title" style={{ cursor: 'default' }}>
                            {ob.org.name}
                          </div>
                          <span className="billing-row-meta">
                            {ob.submissionDetails.length} submission{ob.submissionDetails.length !== 1 ? 's' : ''}
                          </span>
                        </td>
                        <td className="text-right">{ob.totalBillableArtists}</td>
                        <td className="text-right">{formatCurrency(ob.totalSubmissionFees)}</td>
                        <td className="text-right">{ob.memberCount}</td>
                        <td className="text-right">{formatCurrency(ob.memberPlatformFee)}</td>
                        <td className="text-right font-semibold">{formatCurrency(ob.totalPlatformFees)}</td>
                        <td className="text-right muted">{formatCurrency(ob.totalStripeFees)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="billing-total-row">
                      <td><strong>Grand Total</strong></td>
                      <td className="text-right"><strong>{revenueData.grandTotals.artists}</strong></td>
                      <td className="text-right"><strong>{formatCurrency(revenueData.grandTotals.submissionFees)}</strong></td>
                      <td className="text-right"><strong>{revenueData.grandTotals.memberships}</strong></td>
                      <td className="text-right"><strong>{formatCurrency(revenueData.grandTotals.membershipFees)}</strong></td>
                      <td className="text-right"><strong>{formatCurrency(revenueData.grandTotals.platformFees)}</strong></td>
                      <td className="text-right muted"><strong>{formatCurrency(revenueData.grandTotals.stripeFees)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Submission Detail per Org */}
            {revenueData.orgBilling.filter(ob => ob.submissionDetails.length > 0).map((ob) => (
              <div key={ob.org.organization_id} className="billing-section">
                <h2 className="billing-section-title">
                  <FileText size={18} /> {ob.org.name} — Submissions
                </h2>
                <div className="billing-table-wrapper">
                  <table className="billing-table">
                    <thead>
                      <tr>
                        <th className="text-left">Call Out / Exhibition</th>
                        <th>Status</th>
                        <th>Fee Type</th>
                        <th className="text-right">Billable Artists</th>
                        <th className="text-right">Platform Fee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ob.submissionDetails.map((sd) => (
                        <tr key={sd.submission_id}>
                          <td className="text-left">{sd.title}</td>
                          <td><span className={`billing-status ${sd.status}`}>{sd.status}</span></td>
                          <td>{sd.isFree ? 'Free (Member)' : formatCurrency(parseFloat(sd.submission_price) || 0)}</td>
                          <td className="text-right">{sd.billableArtists}</td>
                          <td className="text-right font-semibold">{formatCurrency(sd.platformFee)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}

            {/* Revenue Invoice Summary */}
            <div className="billing-section billing-invoice">
              <h2 className="billing-section-title">
                <DollarSign size={18} /> Revenue Summary
              </h2>
              <div className="invoice-box">
                <div className="invoice-header">
                  <div>
                    <strong>VibrantCast Platform Revenue</strong>
                    <br />
                    <span className="invoice-org">All Client Organizations</span>
                  </div>
                  <div className="invoice-date">
                    As of {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                </div>
                <div className="invoice-lines">
                  <div className="invoice-line">
                    <span>Submission Processing Revenue</span>
                    <span>{formatCurrency(revenueData.grandTotals.submissionFees)}</span>
                  </div>
                  {revenueData.orgBilling.filter(ob => ob.totalSubmissionFees > 0).map(ob => (
                    <div key={ob.org.organization_id} className="invoice-subline">
                      <span>{ob.org.name} ({ob.totalBillableArtists} artists &times; {formatCurrency(parseFloat(ob.org.platform_submission_fee) || DEFAULT_SUBMISSION_FEE)})</span>
                      <span>{formatCurrency(ob.totalSubmissionFees)}</span>
                    </div>
                  ))}
                  <div className="invoice-line">
                    <span>Membership Processing Revenue</span>
                    <span>{formatCurrency(revenueData.grandTotals.membershipFees)}</span>
                  </div>
                  {revenueData.orgBilling.filter(ob => ob.memberCount > 0).map(ob => (
                    <div key={ob.org.organization_id} className="invoice-subline">
                      <span>{ob.org.name} ({ob.memberCount} &times; {formatCurrency(parseFloat(ob.org.platform_membership_fee) || DEFAULT_MEMBERSHIP_FEE)})</span>
                      <span>{formatCurrency(ob.memberPlatformFee)}</span>
                    </div>
                  ))}
                  <div className="invoice-divider" />
                  <div className="invoice-line invoice-total">
                    <span>Total Platform Revenue</span>
                    <span>{formatCurrency(revenueData.grandTotals.platformFees)}</span>
                  </div>
                  <div className="invoice-divider" />
                  <div className="invoice-line invoice-info">
                    <span>Total Collected via Stripe (All Orgs)</span>
                    <span>{formatCurrency(revenueData.grandTotals.collected)}</span>
                  </div>
                  <div className="invoice-line invoice-info">
                    <span>Stripe Processing Fees</span>
                    <span>{formatCurrency(revenueData.grandTotals.stripeFees)}</span>
                  </div>
                  <div className="invoice-line invoice-info">
                    <span>Net Revenue after Stripe Fees</span>
                    <span>{formatCurrency(revenueData.grandTotals.platformFees - revenueData.grandTotals.stripeFees)}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── CLIENT BILLING (normal org view) ────────────────────────────────

  return (
    <div className="billing-container">
      <div className="billing-header">
        <div className="billing-header-left">
          <h1><DollarSign size={24} /> Billing &amp; Accounting</h1>
          <p className="billing-subtitle">
            {selectedOrganization.name} &mdash; {startMonth || endMonth
              ? `Statement for ${startMonth ? new Date(startMonth + '-15').toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'beginning'}${endMonth && endMonth !== startMonth ? ` through ${new Date(endMonth + '-15').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}` : ''}`
              : 'Billing summary & settlement (all time)'}
          </p>
        </div>
        <div className="billing-header-actions no-print">
          <button className="billing-action-btn" onClick={exportCSV} title="Export to CSV">
            <Download size={16} /> Export CSV
          </button>
          <button className="billing-action-btn" onClick={handlePrint} title="Print Statement">
            <Printer size={16} /> Print
          </button>
        </div>
      </div>

      {/* Period Filter */}
      <div className="billing-period-filter no-print">
        <label className="billing-period-label"><Calendar size={16} /> Statement Period:</label>
        <div className="billing-period-options">
          <button
            className={`billing-period-btn ${!startMonth && !endMonth ? 'active' : ''}`}
            onClick={() => { setStartMonth(''); setEndMonth(''); }}
          >
            All Time
          </button>
          <span className="billing-period-separator">|</span>
          <label className="billing-range-label">From</label>
          <input
            type="month"
            className="billing-period-input"
            value={startMonth}
            onChange={(e) => setStartMonth(e.target.value)}
          />
          <label className="billing-range-label">Through</label>
          <input
            type="month"
            className="billing-period-input"
            value={endMonth}
            onChange={(e) => setEndMonth(e.target.value)}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="billing-summary-cards">
        <div className="billing-card">
          <div className="billing-card-icon submissions">
            <FileText size={20} />
          </div>
          <div className="billing-card-content">
            <span className="billing-card-label">Total Collected (Stripe)</span>
            <span className="billing-card-value">
              {formatCurrency(billingData.totalCollected)}
            </span>
          </div>
        </div>
        <div className="billing-card">
          <div className="billing-card-icon total">
            <TrendingUp size={20} />
          </div>
          <div className="billing-card-content">
            <span className="billing-card-label">VibrantCast Platform Fees</span>
            <span className="billing-card-value highlight">
              {formatCurrency(billingData.totalPlatformFees)}
            </span>
          </div>
        </div>
        <div className="billing-card">
          <div className="billing-card-icon memberships">
            <Users size={20} />
          </div>
          <div className="billing-card-content">
            <span className="billing-card-label">Owed to {selectedOrganization.name}</span>
            <span className="billing-card-value">
              {formatCurrency(billingData.totalOwedToOrg)}
            </span>
          </div>
        </div>
        <div className="billing-card">
          <div className="billing-card-icon stripe">
            <CreditCard size={20} />
          </div>
          <div className="billing-card-content">
            <span className="billing-card-label">Outstanding Balance</span>
            <span className={`billing-card-value ${outstandingBalance > 0 ? 'settlement-positive' : outstandingBalance < 0 ? 'settlement-negative' : ''}`}>
              {outstandingBalance > 0
                ? `${selectedOrganization.name} owes ${formatCurrency(outstandingBalance)}`
                : outstandingBalance < 0
                ? `VibrantCast owes ${formatCurrency(Math.abs(outstandingBalance))}`
                : 'Fully settled'}
            </span>
            {totalPaid > 0 && (
              <span className="billing-card-sublabel">{formatCurrency(totalPaid)} paid · gross {formatCurrency(Math.abs(billingData.netSettlement))}</span>
            )}
          </div>
        </div>
      </div>

      {/* How the numbers connect */}
      {(billingData.totalOwedToUs > 0 || billingData.totalOwedToOrg > 0) && (
        <div className="billing-formula-row no-print">
          <div className="billing-formula">
            <span className="formula-step">
              <span className="formula-label">Collected</span>
              <span className="formula-value">{formatCurrency(billingData.totalCollected)}</span>
            </span>
            <span className="formula-op">−</span>
            <span className="formula-step">
              <span className="formula-label">Fees on paid items</span>
              <span className="formula-value">{formatCurrency(billingData.totalPlatformFees - billingData.totalOwedToUs)}</span>
            </span>
            <span className="formula-op">=</span>
            <span className="formula-step">
              <span className="formula-label">Owed to {selectedOrganization.name}</span>
              <span className="formula-value">{formatCurrency(billingData.totalOwedToOrg)}</span>
            </span>
            {billingData.totalOwedToUs > 0 && (
              <>
                <span className="formula-op">−</span>
                <span className="formula-step">
                  <span className="formula-label">Free show fees</span>
                  <span className="formula-value">{formatCurrency(billingData.totalOwedToUs)}</span>
                </span>
              </>
            )}
            {totalPaid > 0 && (
              <>
                <span className="formula-op">−</span>
                <span className="formula-step">
                  <span className="formula-label">Payments made</span>
                  <span className="formula-value">{formatCurrency(totalPaid)}</span>
                </span>
              </>
            )}
            <span className="formula-op">=</span>
            <span className="formula-step formula-result">
              <span className="formula-label">Outstanding</span>
              <span className="formula-value">{outstandingBalance === 0 ? 'Settled' : formatCurrency(Math.abs(outstandingBalance))}</span>
            </span>
          </div>
        </div>
      )}

      {/* Submission Breakdown */}
      <div className="billing-section">
        <h2 className="billing-section-title">
          <FileText size={18} /> Submission Processing Fees
        </h2>
        <p className="billing-section-desc">
          {formatCurrency(billingData.subFee)} per artist submission
        </p>
        <div className="billing-table-wrapper">
          <table className="billing-table">
            <thead>
              <tr>
                <th>Call Out / Exhibition</th>
                <th>Status</th>
                <th>Fee Type</th>
                <th className="text-right">Artists</th>
                <th className="text-right">Collected</th>
                <th className="text-right">Platform Fee</th>
                <th className="text-right">Due to {selectedOrganization.name}</th>
                <th className="text-right">Stripe Fee</th>
              </tr>
            </thead>
            <tbody>
              {billingData.submissionLineItems.map((item) => (
                <React.Fragment key={item.id}>
                  <tr
                    className="billing-row clickable"
                    onClick={() => handleToggleSubmission(item.id, item.isFree)}
                  >
                    <td>
                      <div className="billing-row-title">
                        {expandedSubmission === item.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        {item.title}
                      </div>
                      <span className="billing-row-meta">
                        Collection #{item.collectionId} &middot; Submission #{item.id}
                      </span>
                    </td>
                    <td>
                      <span className={`billing-status ${item.status}`}>{item.status}</span>
                    </td>
                    <td>{item.isFree ? 'Free (Member)' : formatCurrency(item.submissionPrice)}</td>
                    <td className="text-right">{item.billableArtists}</td>
                    <td className="text-right">{formatCurrency(item.totalCollected)}</td>
                    <td className="text-right font-semibold">{formatCurrency(item.platformFee)}</td>
                    <td className="text-right">{formatCurrency(item.owedToOrg)}</td>
                    <td className="text-right muted">{formatCurrency(item.stripeFee)}</td>
                  </tr>
                  {expandedSubmission === item.id && (
                    <tr className="billing-detail-row">
                      <td colSpan={8}>
                        <div className="billing-detail-content">
                          <div className="billing-detail-summary">
                            <span><strong>Billable Artists:</strong> {item.billableArtists}</span>
                            <span><strong>Calculation:</strong> {item.billableArtists} &times; {formatCurrency(billingData.subFee)} = {formatCurrency(item.platformFee)}</span>
                            {!item.isFree && (
                              <span><strong>Stripe:</strong> {item.paidArtists} &times; ({formatCurrency(item.submissionPrice)} &times; {parseFloat(orgDetails?.stripe_fee_percent || 2.9)}% + {formatCurrency(parseFloat(orgDetails?.stripe_fee_fixed ?? 0.30))}) = {formatCurrency(item.stripeFee)}</span>
                            )}
                          </div>
                          {loadingArtists[item.id] ? (
                            <div className="billing-artists-loading">Loading artist details...</div>
                          ) : (
                            <table className="billing-artists-table">
                              <thead>
                                <tr>
                                  <th>#</th>
                                  <th className="text-left">Artist</th>
                                  <th className="text-left">Email</th>
                                  <th>Registered</th>
                                  <th>Submitted</th>
                                  <th>Paid</th>
                                  <th className="text-center">Accepted</th>
                                  <th className="text-right">Fee Collected</th>
                                  <th className="text-right">Platform Fee</th>
                                  {isAdmin && <th className="text-center">Exclude</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {(submissionArtists[item.id] || []).map((artist, idx) => {
                                  const isExcluded = exclusions[`${item.id}-${artist.id}`];
                                  return (
                                    <tr key={artist.id} className={isExcluded ? 'billing-row-excluded' : ''}>
                                      <td className="artist-row-num">{idx + 1}</td>
                                      <td className="artist-name text-left">{artist.first_name} {artist.last_name}</td>
                                      <td className="billing-email text-left">{artist.email}</td>
                                      <td>{artist.created_at ? new Date(artist.created_at).toLocaleDateString() : '—'}</td>
                                      <td>{artist.submitted_at ? new Date(artist.submitted_at).toLocaleDateString() : '—'}</td>
                                      <td>
                                        {isExcluded ? (
                                          <span className="billing-status inactive">Excluded</span>
                                        ) : item.isFree ? (
                                          <span className="billing-status active">Free</span>
                                        ) : artist.paid ? (
                                          <span className="billing-status active">Yes</span>
                                        ) : (
                                          <span className="billing-status inactive">No</span>
                                        )}
                                      </td>
                                      <td className="text-center">
                                        {artist.acceptedArtworks > 0 ? (
                                          <span className="billing-status active">{artist.acceptedArtworks} of {artist.totalArtworks}</span>
                                        ) : artist.totalArtworks > 0 ? (
                                          <span className="billing-status inactive">0 of {artist.totalArtworks}</span>
                                        ) : (
                                          <span className="billing-status">—</span>
                                        )}
                                      </td>
                                      <td className="text-right">
                                        {isExcluded ? '—' : item.isFree ? '—' : artist.paid ? formatCurrency(item.submissionPrice) : '—'}
                                      </td>
                                      <td className="text-right font-semibold">
                                        {isExcluded ? '—' : (item.isFree || artist.paid) ? formatCurrency(billingData.subFee) : '—'}
                                      </td>
                                      {isAdmin && (
                                        <td className="text-center">
                                          <button
                                            className={`billing-exclude-btn ${isExcluded ? 'excluded' : ''}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleExclusion(item.id, artist.id, `${artist.first_name} ${artist.last_name}`);
                                            }}
                                            title={isExcluded ? 'Include in billing' : 'Exclude from billing'}
                                          >
                                            {isExcluded ? 'Include' : 'Exclude'}
                                          </button>
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr className="billing-total-row">
                <td colSpan={3}><strong>Submission Totals</strong></td>
                <td className="text-right">
                  <strong>{billingData.submissionLineItems.reduce((s, i) => s + i.billableArtists, 0)}</strong>
                </td>
                <td className="text-right">
                  <strong>{formatCurrency(billingData.submissionLineItems.reduce((s, i) => s + i.totalCollected, 0))}</strong>
                </td>
                <td className="text-right">
                  <strong>{formatCurrency(billingData.submissionLineItems.reduce((s, i) => s + i.platformFee, 0))}</strong>
                </td>
                <td className="text-right">
                  <strong>{formatCurrency(billingData.submissionLineItems.reduce((s, i) => s + i.owedToOrg, 0))}</strong>
                </td>
                <td className="text-right muted">
                  <strong>{formatCurrency(billingData.submissionLineItems.reduce((s, i) => s + i.stripeFee, 0))}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Membership Breakdown */}
      <div className="billing-section">
        <h2 className="billing-section-title">
          <Users size={18} /> Membership Processing Fees
        </h2>
        <p className="billing-section-desc">
          {formatCurrency(billingData.memFee)} per membership sold through platform
        </p>
        <div className="billing-table-wrapper">
          <table className="billing-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Email</th>
                <th>Join Date</th>
                <th>Status</th>
                <th className="text-right">Membership Fee</th>
                <th className="text-right">Platform Fee</th>
              </tr>
            </thead>
            <tbody>
              {memberships.length === 0 ? (
                <tr>
                  <td colSpan={6} className="billing-empty-row">No memberships sold through platform yet.</td>
                </tr>
              ) : (
                memberships.map((m) => (
                  <tr key={m.id}>
                    <td>{m.first_name} {m.last_name}</td>
                    <td className="billing-email">{m.email}</td>
                    <td>{m.join_date ? new Date(m.join_date).toLocaleDateString() : '—'}</td>
                    <td>
                      <span className={`billing-status ${m.membership_status}`}>{m.membership_status}</span>
                    </td>
                    <td className="text-right">{formatCurrency(billingData.membershipFee)}</td>
                    <td className="text-right font-semibold">{formatCurrency(billingData.memFee)}</td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="billing-total-row">
                <td colSpan={4}>
                  <strong>Membership Totals ({billingData.membershipCount} sold)</strong>
                </td>
                <td className="text-right">
                  <strong>{formatCurrency(billingData.membershipTotalCollected)}</strong>
                </td>
                <td className="text-right">
                  <strong>{formatCurrency(billingData.membershipPlatformFee)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Invoice Summary */}
      <div className="billing-section billing-invoice">
        <h2 className="billing-section-title">
          <DollarSign size={18} /> Settlement Summary
        </h2>
        <div className="invoice-box">
          <div className="invoice-header">
            <div>
              <strong>VibrantCast ↔ {selectedOrganization.name}</strong>
              <br />
              <span className="invoice-org">{startMonth || endMonth ? 'Period Statement' : 'Cumulative Settlement'}</span>
            </div>
            <div className="invoice-date">
              {startMonth || endMonth
                ? `${startMonth ? new Date(startMonth + '-15').toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'Beginning'} through ${endMonth ? new Date(endMonth + '-15').toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : 'Present'}`
                : `As of ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`}
            </div>
          </div>
          <div className="invoice-lines">
            {/* Section A: What VibrantCast collected via Stripe */}
            <div className="invoice-line">
              <span><strong>Collected via Stripe</strong></span>
              <span><strong>{formatCurrency(billingData.totalCollected)}</strong></span>
            </div>
            {billingData.submissionLineItems.filter(i => !i.isFree && i.totalCollected > 0).map(item => (
              <div key={item.id} className="invoice-subline">
                <span>{item.title} ({item.paidArtists} artists &times; {formatCurrency(item.submissionPrice)})</span>
                <span>{formatCurrency(item.totalCollected)}</span>
              </div>
            ))}
            {billingData.membershipCount > 0 && (
              <div className="invoice-subline">
                <span>Memberships ({billingData.membershipCount} &times; {formatCurrency(billingData.membershipFee)})</span>
                <span>{formatCurrency(billingData.membershipTotalCollected)}</span>
              </div>
            )}
            <div className="invoice-divider" />

            {/* Section B: VibrantCast keeps (platform fees) */}
            <div className="invoice-line">
              <span><strong>VibrantCast Platform Fees</strong></span>
              <span><strong>{formatCurrency(billingData.totalPlatformFees)}</strong></span>
            </div>
            {billingData.submissionLineItems.map(item => (
              <div key={item.id} className="invoice-subline">
                <span>{item.title} ({item.billableArtists} artists &times; {formatCurrency(billingData.subFee)})</span>
                <span>{formatCurrency(item.platformFee)}</span>
              </div>
            ))}
            {billingData.membershipCount > 0 && (
              <div className="invoice-subline">
                <span>Membership fees ({billingData.membershipCount} &times; {formatCurrency(billingData.memFee)})</span>
                <span>{formatCurrency(billingData.membershipPlatformFee)}</span>
              </div>
            )}
            <div className="invoice-divider" />

            {/* Section C: Stripe fees */}
            <div className="invoice-line invoice-info">
              <span>Stripe Processing Fees ({billingData.passStripe ? `Charged to ${selectedOrganization.name}` : 'Absorbed by VibrantCast'})</span>
              <span>{formatCurrency(billingData.totalStripeFees)}</span>
            </div>
            <div className="invoice-divider" />

            {/* Section D: Owed to org from collected funds */}
            {billingData.totalOwedToOrg > 0 && (
              <div className="invoice-line">
                <span><strong>Owed to {selectedOrganization.name}</strong> (Collected − Platform Fee{billingData.passStripe ? ' − Stripe' : ''})</span>
                <span><strong>{formatCurrency(billingData.totalOwedToOrg)}</strong></span>
              </div>
            )}
            {billingData.submissionLineItems.filter(i => i.owedToOrg > 0).map(item => (
              <div key={item.id} className="invoice-subline">
                <span>{item.title} ({formatCurrency(item.totalCollected)} − {formatCurrency(item.platformFee)}{billingData.passStripe ? ` − ${formatCurrency(item.stripeFee)}` : ''})</span>
                <span>{formatCurrency(item.owedToOrg)}</span>
              </div>
            ))}
            {billingData.membershipOwedToOrg > 0 && (
              <div className="invoice-subline">
                <span>Memberships ({formatCurrency(billingData.membershipTotalCollected)} − {formatCurrency(billingData.membershipPlatformFee)}{billingData.passStripe ? ` − ${formatCurrency(billingData.membershipStripeFee)}` : ''})</span>
                <span>{formatCurrency(billingData.membershipOwedToOrg)}</span>
              </div>
            )}

            {/* Section E: Org owes us for free submissions */}
            {billingData.totalOwedToUs > 0 && (
              <>
                <div className="invoice-divider" />
                <div className="invoice-line">
                  <span><strong>{selectedOrganization.name} Owes VibrantCast</strong> (Free submission platform fees)</span>
                  <span><strong>{formatCurrency(billingData.totalOwedToUs)}</strong></span>
                </div>
                {billingData.submissionLineItems.filter(i => i.owedToUs > 0).map(item => (
                  <div key={item.id} className="invoice-subline">
                    <span>{item.title} ({item.billableArtists} artists &times; {formatCurrency(billingData.subFee)})</span>
                    <span>{formatCurrency(item.owedToUs)}</span>
                  </div>
                ))}
              </>
            )}

            {/* Section F: Net Settlement & Payments */}
            <div className="invoice-divider" />
            <div className={`invoice-line invoice-total ${billingData.netSettlement > 0 ? 'settlement-positive' : billingData.netSettlement < 0 ? 'settlement-negative' : ''}`}>
              <span>
                {billingData.netSettlement > 0
                  ? `Net: ${selectedOrganization.name} Owes VibrantCast`
                  : billingData.netSettlement < 0
                  ? `Net: VibrantCast Owes ${selectedOrganization.name}`
                  : 'Net Settlement'}
              </span>
              <span>{formatCurrency(Math.abs(billingData.netSettlement))}</span>
            </div>
            {totalPaid > 0 && (
              <>
                <div className="invoice-line invoice-info">
                  <span>Payments Made to {selectedOrganization.name}</span>
                  <span>− {formatCurrency(totalPaid)}</span>
                </div>
                <div className="invoice-divider" />
                <div className={`invoice-line invoice-total ${outstandingBalance > 0 ? 'settlement-positive' : outstandingBalance < 0 ? 'settlement-negative' : ''}`}>
                  <span>
                    {outstandingBalance > 0
                      ? `Outstanding: ${selectedOrganization.name} Owes VibrantCast`
                      : outstandingBalance < 0
                      ? `Outstanding: VibrantCast Owes ${selectedOrganization.name}`
                      : 'Outstanding Balance: Fully Settled'}
                  </span>
                  <span>{outstandingBalance !== 0 ? formatCurrency(Math.abs(outstandingBalance)) : '—'}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Reconciliation History (admin only) */}
      {isAdmin && (
        <div className="billing-section no-print">
          <h2 className="billing-section-title">
            <CheckCircle size={18} /> Monthly Reconciliation
          </h2>
          <p className="billing-section-desc">
            Track when each month's billing has been reconciled and invoiced in QuickBooks.
          </p>

          {/* Reconcile button */}
          {!showReconcileForm ? (
            <button
              className="billing-action-btn reconcile-btn"
              onClick={() => {
                // Default to end of selected range, or current month
                const now = new Date();
                const defaultMonth = endMonth || startMonth || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                setReconcileData({ period_month: defaultMonth, quickbooks_invoice_number: '', notes: '', amount_paid: '' });
                setShowReconcileForm(true);
              }}
            >
              <Calendar size={16} /> Record Reconciliation
            </button>
          ) : (
            <div className="reconcile-form">
              <div className="reconcile-form-fields">
                <div className="reconcile-field">
                  <label>Billing Period</label>
                  <input
                    type="month"
                    value={reconcileData.period_month}
                    onChange={(e) => setReconcileData(prev => ({ ...prev, period_month: e.target.value }))}
                    required
                  />
                </div>
                <div className="reconcile-field">
                  <label>QuickBooks Invoice #</label>
                  <input
                    type="text"
                    placeholder="e.g., INV-1234"
                    value={reconcileData.quickbooks_invoice_number}
                    onChange={(e) => setReconcileData(prev => ({ ...prev, quickbooks_invoice_number: e.target.value }))}
                  />
                </div>
                <div className="reconcile-field wide">
                  <label>Notes (optional)</label>
                  <input
                    type="text"
                    placeholder="e.g., Paid via ACH on 3/15"
                    value={reconcileData.notes}
                    onChange={(e) => setReconcileData(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>
                <div className="reconcile-field">
                  <label>Amount Paid (ACH / Check)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g., 461.25"
                    value={reconcileData.amount_paid}
                    onChange={(e) => setReconcileData(prev => ({ ...prev, amount_paid: e.target.value }))}
                  />
                </div>
              </div>
              <div className="reconcile-form-actions">
                <button className="billing-action-btn" onClick={() => setShowReconcileForm(false)}>Cancel</button>
                <button
                  className="billing-action-btn reconcile-btn"
                  onClick={saveReconciliation}
                  disabled={reconcileSaving || !reconcileData.period_month}
                >
                  <CheckCircle size={14} />
                  {reconcileSaving ? 'Saving...' : 'Save Reconciliation'}
                </button>
              </div>
            </div>
          )}

          {/* Reconciliation History Table */}
          {reconciliations.length > 0 && (
            <div className="billing-table-wrapper" style={{ marginTop: '1rem' }}>
              <table className="billing-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>QuickBooks Invoice</th>
                    <th className="text-right">Collected</th>
                    <th className="text-right">Platform Fees</th>
                    <th className="text-right">Net Settlement</th>
                    <th className="text-right">Paid</th>
                    <th>Reconciled</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {reconciliations.map(r => {
                    const [year, month] = r.period_month.split('-');
                    const periodLabel = new Date(year, month - 1).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
                    return (
                      <tr key={r.id}>
                        <td>{periodLabel}</td>
                        <td>
                          {r.quickbooks_invoice_number ? (
                            <span className="billing-status active">{r.quickbooks_invoice_number}</span>
                          ) : (
                            <span className="billing-status inactive">No invoice</span>
                          )}
                        </td>
                        <td className="text-right">{formatCurrency(r.total_collected)}</td>
                        <td className="text-right">{formatCurrency(r.platform_fees)}</td>
                        <td className="text-right">
                          <span className={r.net_settlement > 0 ? 'settlement-positive' : r.net_settlement < 0 ? 'settlement-negative' : ''}>
                            {r.net_settlement > 0
                              ? `${selectedOrganization.name} owes ${formatCurrency(r.net_settlement)}`
                              : r.net_settlement < 0
                              ? `VibrantCast owes ${formatCurrency(Math.abs(r.net_settlement))}`
                              : 'Settled'}
                          </span>
                        </td>
                        <td className="text-right">
                          {r.amount_paid ? (
                            <span className="billing-status active">{formatCurrency(r.amount_paid)}</span>
                          ) : (
                            <span className="billing-status inactive">—</span>
                          )}
                        </td>
                        <td>{r.reconciled_at ? new Date(r.reconciled_at).toLocaleDateString() : '—'}</td>
                        <td className="billing-email">{r.notes || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CuratorBilling;
